import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useChangePassword } from '../../hooks/useChangePassword';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

interface CambioPasswordModalProps {
  /** Si el modal está abierto */
  isOpen: boolean;
  /** Función para cerrar el modal (también se llama tras un cambio exitoso) */
  onClose: () => void;
  /** Cambio obligatorio: no se puede cerrar sin cambiar la contraseña */
  required?: boolean;
  /** Cierra la sesión (solo en modo obligatorio) */
  onLogout?: () => void;
}

/**
 * Modal para cambiar la contraseña del usuario
 */
export const CambioPasswordModal: React.FC<CambioPasswordModalProps> = ({
  isOpen,
  onClose,
  required = false,
  onLogout
}) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const { changePassword, isLoading, error, success, clearState } = useChangePassword();

  /**
   * Limpia el formulario y estado cuando se abre/cierra el modal
   */
  useEffect(() => {
    if (isOpen) {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});
      clearState();
    }
  }, [isOpen, clearState]);

  /**
   * Cierra el modal después de un cambio exitoso
   */
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, onClose]);

  /**
   * Actualiza un campo del formulario
   */
  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Alterna la visibilidad de una contraseña
   */
  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  /**
   * Valida el formulario
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'La nueva contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmar la nueva contraseña es requerido';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'La nueva contraseña debe ser diferente a la actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
    } catch (err) {
      // El error ya se maneja en el hook
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={required ? () => {} : onClose}
      size="md"
    >
      {/* Header personalizado */}
      <div className="mb-6 pb-4 border-b border-secondary-700">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Lock className="w-5 h-5 text-emerald-400" />
          {required ? 'Cambio de Contraseña Requerido' : 'Cambiar Contraseña'}
        </h2>
        {required && (
          <p className="mt-2 text-sm text-secondary-300">
            Por seguridad, debes cambiar tu contraseña antes de continuar
          </p>
        )}
      </div>

      {/* Mensaje de éxito */}
      {success && (
        <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <span className="text-emerald-100 text-sm">
            ¡Contraseña cambiada exitosamente!
          </span>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-100 text-sm">
            {error}
          </span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mensaje de éxito */}
        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-green-400 font-medium">¡Contraseña cambiada exitosamente!</p>
              <p className="text-green-300 text-sm">
                {required ? 'Entrando en la aplicación...' : 'El modal se cerrará automáticamente...'}
              </p>
            </div>
          </div>
        )}

        {/* Mensaje de error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Contraseña actual */}
        <div className="relative">
          <Input
            label="Contraseña Actual *"
            type={showPasswords.current ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(e) => updateField('currentPassword', e.target.value)}
            placeholder="Ingresa tu contraseña actual"
            error={errors.currentPassword}
            icon={<Lock size={20} />}
            disabled={isLoading || success}
            required
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('current')}
            className="absolute right-3 top-9 text-secondary-400 hover:text-secondary-300 transition-colors"
            disabled={isLoading || success}
          >
            {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {/* Nueva contraseña */}
        <div className="relative">
          <Input
            label="Nueva Contraseña *"
            type={showPasswords.new ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={(e) => updateField('newPassword', e.target.value)}
            placeholder="Ingresa tu nueva contraseña"
            error={errors.newPassword}
            icon={<Lock size={20} />}
            disabled={isLoading || success}
            required
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute right-3 top-9 text-secondary-400 hover:text-secondary-300 transition-colors"
            disabled={isLoading || success}
          >
            {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {/* Confirmar nueva contraseña */}
        <div className="relative">
          <Input
            label="Confirmar Nueva Contraseña *"
            type={showPasswords.confirm ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => updateField('confirmPassword', e.target.value)}
            placeholder="Confirma tu nueva contraseña"
            error={errors.confirmPassword}
            icon={<Lock size={20} />}
            disabled={isLoading || success}
            required
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute right-3 top-9 text-secondary-400 hover:text-secondary-300 transition-colors"
            disabled={isLoading || success}
          >
            {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {/* Información de seguridad */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <h4 className="text-blue-400 font-medium mb-2">Requisitos de la contraseña:</h4>
          <ul className="text-blue-300 text-sm space-y-1">
            <li>• Mínimo 6 caracteres</li>
            <li>• Debe ser diferente a la contraseña actual</li>
            <li>• Se recomienda usar una combinación de letras, números y símbolos</li>
          </ul>
        </div>

        {/* Botones */}
        <div className="flex gap-3 pt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={required ? onLogout : onClose}
            disabled={isLoading}
            className="flex-1"
          >
            {required ? 'Cerrar sesión' : 'Cancelar'}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || success}
            className="flex-1"
          >
            {isLoading ? 'Cambiando...' : success ? 'Cambiada' : 'Cambiar Contraseña'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};